import { useEffect } from 'react';
import { useLocation, useMatches } from 'react-router';

export interface SEOConfig {
  title?: string;
  description?: string;
  ogType?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogUrl?: string;
  canonical?: string;
  robots?: string;
  lang?: string;
  hreflangs?: string[];
}

const HREFLANG_MAP: Record<string, string> = {
  en: 'en',
  ja: 'ja',
  zh: 'zh-Hans',
  zt: 'zh-Hant',
  ko: 'ko'
};

const swapLocale = (pathname: string, locale: string): string => {
  const segments = pathname.split('/');
  segments[1] = locale;
  return segments.join('/');
};

const updateMetaTag = (
  name: string,
  content: string,
  property = false
): void => {
  const attribute = property ? 'property' : 'name';
  let element = document.querySelector(
    `meta[${attribute}="${name}"]`
  ) as HTMLMetaElement;

  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attribute, name);
    document.head.appendChild(element);
  }

  element.content = content;
};

const updateLinkTag = (rel: string, href: string): void => {
  if (!href) return;

  let element = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement;

  if (!element) {
    element = document.createElement('link');
    element.rel = rel;
    document.head.appendChild(element);
  }

  element.href = href;
};

const buildCanonical = (
  pathname: string,
  search: string,
  matches: ReturnType<typeof useMatches>
) => {
  const base = `${window.location.origin}${pathname}`;
  const handle = matches[matches.length - 1]?.handle as
    | { canonicalQueryKeys?: string[] }
    | undefined;
  const keys = handle?.canonicalQueryKeys;
  if (!keys?.length) {
    return base;
  }
  const current = new URLSearchParams(search);
  const kept = new URLSearchParams();
  keys.forEach(key => {
    const value = current.get(key);
    if (value) {
      kept.set(key, value);
    }
  });
  const query = kept.toString();
  return query ? `${base}?${query}` : base;
};

// Captured once at import, while the title is still index.html's site name —
// Base and useTitle overwrite it later, so reading it lazily would pick up a
// page title instead. There is no document during a server render; the hook's
// effects never run there, so the empty fallback is never used, but without it
// importing the hooks barrel on the server throws.
const SITE_NAME = typeof document === 'undefined' ? '' : document.title;

const EMPTY_CONFIG: SEOConfig = {};

export function useSEO(config: SEOConfig = EMPTY_CONFIG) {
  const { pathname, search } = useLocation();
  const matches = useMatches();

  useEffect(() => {
    const {
      title,
      description,
      ogType,
      ogTitle,
      ogDescription,
      ogImage,
      ogUrl,
      canonical,
      robots,
      lang
    } = config;

    // Only touch content tags when the page actually provides SEO content, so
    // a bare useSEO() call (e.g. from a layout) manages the canonical link only
    // and doesn't clobber per-page title/description/og with empty values.
    const hasContent =
      !!title ||
      !!description ||
      !!ogType ||
      !!ogTitle ||
      !!ogDescription ||
      !!ogImage ||
      !!ogUrl ||
      !!robots ||
      !!lang;

    if (hasContent) {
      // Update title as "<page title> - <site name>".
      if (title) {
        document.title = SITE_NAME ? `${title} - ${SITE_NAME}` : title;
      }

      // Update HTML lang attribute
      if (lang) {
        document.documentElement.lang = lang;
      }

      // Basic meta tags
      updateMetaTag('description', description || '');
      updateMetaTag('robots', robots || 'index, follow');

      // Open Graph meta tags
      updateMetaTag('og:type', ogType || 'website', true);
      updateMetaTag('og:title', ogTitle || title || '', true);
      updateMetaTag('og:description', ogDescription || description || '', true);
      updateMetaTag('og:image', ogImage || '', true);
      updateMetaTag('og:url', ogUrl || window.location.href, true);
    }

    // Self-referencing canonical. Query/hash are dropped by default so pages
    // differing only by tracking/filter params collapse to one canonical URL.
    // A route may keep specific query params via handle.canonicalQueryKeys.
    updateLinkTag(
      'canonical',
      canonical || buildCanonical(pathname, search, matches)
    );

    const hreflangLinks: HTMLLinkElement[] = (config.hreflangs ?? []).map(
      locale => {
        const link = document.createElement('link');
        link.rel = 'alternate';
        link.hreflang = HREFLANG_MAP[locale] ?? locale;
        link.href = buildCanonical(
          swapLocale(pathname, locale),
          search,
          matches
        );
        document.head.appendChild(link);
        return link;
      }
    );

    return () => {
      hreflangLinks.forEach(link => link.remove());
      if (title) {
        document.title = SITE_NAME;
      }
    };
  }, [config, pathname, search, matches]);
}
